/** App paths for fix sessions. Client-safe. */
import type { Route } from 'next';

import type { FixSessionSummary } from './types';

import { buildCodeSessionRunId } from './run-id';

import { buildCodebaseScanPath, buildPrPath } from '@/utils/app-paths';

export const FIX_SESSIONS_PATH = '/code/fix';

/**
 * The name of the list at `FIX_SESSIONS_PATH`. It holds all fix-session/coding-agent runs, from
 * Fix with cubic, chat requests and codebase scans as well as auto-fix, plus the dead-code checks
 * and demos cubic starts for authors. So it isn't named after the Auto-fix settings tab, or after
 * fixes alone.
 */
export const FIX_SESSIONS_LABEL = 'Automations';

export function buildFixSessionPath(sandboxId: string): string {
  return `${FIX_SESSIONS_PATH}/${encodeURIComponent(sandboxId)}`;
}

/**
 * cubic's session on a pull request, named by the pull request instead of a run: it opens the
 * session's latest run, and before any run reached a session, says what cubic waits for. Turning
 * auto-fix on lands here, whether or not a run starts.
 */
export function buildPullRequestFixSessionPath(pr: {
  owner: string;
  repo: string;
  pullNumber: number;
}): string {
  return [
    FIX_SESSIONS_PATH,
    'pr',
    encodeURIComponent(pr.owner),
    encodeURIComponent(pr.repo),
    encodeURIComponent(String(pr.pullNumber)),
  ].join('/');
}

/**
 * Where a code session cubic owns opens instead of `/code/<id>`: its latest run's page, which
 * shows the whole session read-only; the fix sessions list for a session without runs.
 */
export function cubicSessionPath(turns: readonly { id: string; index: number }[]): string {
  const latest = turns.reduce<{ id: string; index: number } | undefined>(
    (found, turn) => (!found || turn.index > found.index ? turn : found),
    undefined,
  );
  return latest ? buildFixSessionPath(buildCodeSessionRunId(latest.id)) : FIX_SESSIONS_PATH;
}

export type FixSessionSubjectLinks = {
  /** In cubic: the PR page, or the scan with the finding open. */
  appHref: Route;
  /** GitHub: the PR scrolled to cubic's comment when known, else the PR; null for scan runs. */
  githubHref: string | null;
};

/** Where a session's subject lives. The only place that turns a subject into URLs. */
export function fixSessionSubjectLinks(
  session: Pick<FixSessionSummary, 'repoFullName' | 'subject'>,
): FixSessionSubjectLinks {
  const { subject } = session;
  if (subject.type === 'codebase_scan') {
    return {
      appHref: buildCodebaseScanPath({
        repoScanId: subject.repoScanId,
        violationId: subject.repoScanViolationId,
      }),
      githubHref: null,
    };
  }
  const [owner = '', repo = ''] = session.repoFullName.split('/');
  const pullRequestUrl = `https://github.com/${session.repoFullName}/pull/${subject.pullNumber}`;
  return {
    appHref: buildPrPath({ owner, repo, number: subject.pullNumber }),
    githubHref: subject.commentAnchor
      ? `${pullRequestUrl}#${subject.commentAnchor}`
      : pullRequestUrl,
  };
}

/**
 * The commits this run pushed itself, as one way to review them on GitHub: the commit, or the
 * compare view from the first one's parent to the last, which holds exactly the run's commits and
 * none of the pull request's others.
 */
export function pushedCommitsHref(
  repoFullName: string,
  shas: readonly [string, ...string[]],
): string {
  const [first] = shas;
  const last = shas[shas.length - 1] ?? first;
  return shas.length === 1
    ? `https://github.com/${repoFullName}/commit/${first}`
    : `https://github.com/${repoFullName}/compare/${first}%5E...${last}`;
}
